"use client";

import * as React from "react";
import { cva } from "class-variance-authority";
import { RadioGroup as RadioGroupPrimitive } from "radix-ui";

import { cn } from "@immi/ui/lib/utils";

const rootVariants = cva("", {
  variants: {
    variant: {
      segmented:
        "inline-flex w-fit rounded-[9px] border border-neutral-100 bg-neutral-50 p-0.5",
      chips: "flex flex-wrap gap-1.5",
    },
  },
  defaultVariants: { variant: "segmented" },
});

const itemVariants = cva(
  "relative inline-flex min-h-9 items-center justify-center text-sm font-semibold transition-colors outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:pointer-events-none disabled:opacity-60",
  {
    variants: {
      variant: {
        segmented:
          "rounded-[7px] px-3 text-ink-muted after:absolute after:inset-x-0 after:-inset-y-1 after:content-[''] hover:text-ink-body data-[state=checked]:bg-card data-[state=checked]:text-ink-headline data-[state=checked]:shadow-sm",
        chips:
          "rounded-lg border border-neutral-200 px-3 text-ink-muted hover:text-ink-body data-[state=checked]:border-primary-600 data-[state=checked]:bg-primary-50 data-[state=checked]:text-primary-700",
      },
    },
    defaultVariants: { variant: "segmented" },
  },
);

type SegmentedOption<T extends string> = {
  value: T;
  label: React.ReactNode;
  disabled?: boolean;
};

type SegmentedControlProps<T extends string> = Omit<
  React.ComponentProps<typeof RadioGroupPrimitive.Root>,
  "value" | "defaultValue" | "onValueChange" | "orientation" | "children"
> & {
  options: ReadonlyArray<SegmentedOption<T>>;
  /** `null` leaves every option unchecked. */
  value: T | null;
  onValueChange: (value: T) => void;
  /** `segmented`: one pill track. `chips`: separate outlined chips that wrap. */
  variant?: "segmented" | "chips";
  itemClassName?: string;
};

/**
 * One choice among a few, shown side by side. A radio group underneath:
 * one tab stop, arrow keys move and select, Space selects.
 */
function SegmentedControl<T extends string>({
  options,
  value,
  onValueChange,
  variant = "segmented",
  className,
  itemClassName,
  ...props
}: SegmentedControlProps<T>) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="segmented-control"
      orientation="horizontal"
      loop
      value={value ?? ""}
      onValueChange={(next) => onValueChange(next as T)}
      className={cn(rootVariants({ variant }), className)}
      {...props}
    >
      {options.map((option) => (
        <RadioGroupPrimitive.Item
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          data-slot="segmented-control-item"
          className={cn(itemVariants({ variant }), itemClassName)}
        >
          {option.label}
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
}

export { SegmentedControl };
export type { SegmentedControlProps, SegmentedOption };
